import React, { useEffect, useState, useCallback } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import API_BASE_URL from '../../../config/api';
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

function GuidesContainer() {
  const { user } = useAuth();
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInterestIndex, setOpenInterestIndex] = useState(null);

  // New state variables for search and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGuides, setTotalGuides] = useState(0);

  // Debounce search input
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 9,
        department: selectedDepartment,
        search: debouncedSearch
      });

      const response = await axios.get(`${API_BASE_URL}/api/guides?${queryParams}`, {
        headers: {
          "Authorization": `Bearer ${user?.token}`,
        },
      });
      const data = response.data;

      if (response.status === 200) {
        // Handle both old array format (fallback) and new object format
        if (Array.isArray(data)) {
          setGuides(data);
          setTotalPages(1);
          setTotalGuides(data.length);
        } else {
          setGuides(data.guides || []);
          setTotalPages(data.totalPages || 1);
          setTotalGuides(data.totalGuides || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching guides:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment, debouncedSearch, currentPage]);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
      mirror: true
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [selectedDepartment, debouncedSearch]);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className='min-h-screen p-6 md:p-12'>
      <div className='max-w-7xl mx-auto'>

        <div className='grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-8'>

          {/* Main Content Area */}
          <div className='flex flex-col gap-6'>

            {/* Search Bar */}
            <div className='bg-white px-4 py-2 rounded-full shadow-sm transition-all duration-300 hover:shadow-md focus-within:shadow-md flex items-center gap-3 max-w-7xl mx-auto w-full'>
              <FaSearch className='text-gray-400 text-lg' />
              <input
                type="text"
                placeholder="Search by faculty name or area of interest..."
                className="w-full border-none text-gray-700 outline-none focus:outline-none focus:ring-0 placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Mobile Filter */}
            <div className='lg:hidden'>
              <select
                className='w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] shadow-sm appearance-none'
                value={selectedDepartment}
                onChange={(e) => handleDepartmentSelect(e.target.value)}
              >
                <option value='All'>All Departments</option>
                <option value='Computer Engineering'>Computer Engineering</option>
                <option value='Information Technology'>Information Technology</option>
                <option value='Mechanical Engineering'>Mechanical Engineering</option>
                <option value='Electronics and Telecommunication Engineering'>Electronics and Telecommunication Engineering</option>
              </select>
            </div>

            {/* Guide Cards Grid */}
            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-h-[500px] content-start'>
              {loading ? (
                <div className='col-span-full flex flex-col items-center justify-center py-12 text-gray-500 font-medium'>
                  <div className="w-10 h-10 border-4 border-gray-200 border-t-[#b7202e] rounded-full animate-spin mb-4"></div>
                  Loading guides...
                </div>
              ) : guides.length === 0 ? (
                <div className='col-span-full text-center py-12 text-gray-500 font-medium bg-white rounded-xl p-8'>
                  <div className="text-4xl mb-3">🔍</div>
                  No guides found matching your criteria.
                </div>
              ) : (
                guides.map((guide, index) => (
                  <div
                    key={index}
                    className='bg-white rounded-xl p-6 shadow-sm flex flex-col items-center relative overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-lg hover:shadow-red-900/10 group pt-8'
                  >
                    {/* Top Border */}
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-[#b7202e]"></div>

                    <div className="mb-4 relative">
                      <div className="w-32 h-32 rounded-full border-4 border-white shadow-md bg-gradient-to-br from-rose-50 to-pink-100 flex items-center justify-center">
                        <span className="text-black text-3xl font-bold select-none"> {guide.name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()} </span>
                      </div>
                    </div>

                    <div className='text-xl font-bold text-[#b7202e] mb-2 text-center group-hover:scale-105 transition-transform duration-200'>
                      {guide.name}
                    </div>

                    <div className='inline-block px-4 py-1.5 bg-[#b7202e]/10 text-[#b7202e] rounded-full text-sm font-medium mb-3 text-center'>
                      {guide.department}
                    </div>

                    <div className='w-full text-sm text-gray-600 mb-3 bg-gray-50 p-3 rounded-lg'>
                      <button
                        className="w-full flex items-center justify-between font-semibold text-gray-800"
                        onClick={() =>
                          setOpenInterestIndex(openInterestIndex === index ? null : index)
                        }
                      >
                        <span>Areas of Interest</span>
                        <span
                          className={`transition-transform duration-300 ${openInterestIndex === index ? "rotate-180" : ""
                            }`}
                        >
                          ▼
                        </span>
                      </button>

                      {openInterestIndex === index && (
                        <div className="mt-3">
                          {Array.isArray(guide.areaOfInterest) && guide.areaOfInterest.length > 0 ? (
                            <div className="flex flex-wrap justify-center gap-1.5">
                              {guide.areaOfInterest.map((area, idx) => (
                                <span
                                  key={idx}
                                  className="bg-white border border-gray-200 px-2 py-0.5 rounded text-xs text-gray-500"
                                >
                                  {area}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              No areas specified
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className='text-sm text-gray-500 break-all text-center mt-auto'>
                      {guide.email}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
              <div className='flex justify-center items-center gap-4 mt-8'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className='p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-[#b7202e] hover:border-[#b7202e] disabled:opacity-50 disabled:cursor-not-allowed transition-all'
                >
                  <FaChevronLeft />
                </button>

                <span className='font-medium text-gray-600'>
                  Page <span className='text-[#b7202e] font-bold'>{currentPage}</span> of {totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className='p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-[#b7202e] hover:border-[#b7202e] disabled:opacity-50 disabled:cursor-not-allowed transition-all'
                >
                  <FaChevronRight />
                </button>
              </div>
            )}

          </div>

          {/* Right Filter Sidebar */}
          <div className='hidden lg:block h-fit'>
            <div className='p-6 bg-red-50/30 rounded-xl border-l-4 border-l-[#b7202e] sticky top-6'>
              <ul className='space-y-2'>
                <li className='font-bold text-gray-800 mb-4 px-3 text-lg'>Filter By Department:</li>
                {[
                  { label: 'All Departments', value: 'All' },
                  { label: 'Computer Engineering', value: 'Computer Engineering' },
                  { label: 'Information Technology', value: 'Information Technology' },
                  { label: 'Mechanical Engineering', value: 'Mechanical Engineering' },
                  { label: 'Electronics & Telecom', value: 'Electronics and Telecommunication Engineering' }
                ].map((item, index) => (
                  <li
                    key={index}
                    className={`px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200 hover:translate-x-1 ${selectedDepartment === item.value ? 'text-[#b7202e] font-bold bg-white shadow-sm' : 'text-gray-600 hover:text-[#b7202e] hover:bg-[#b7202e]/5'}`}
                    onClick={() => handleDepartmentSelect(item.value)}
                  >
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GuidesContainer;
